import type { FieldValues, Path } from 'react-hook-form';
import { Controller, useFormContext, useFormState } from 'react-hook-form';

import {
  formatCurrencyDisplay,
  parseCurrencyToStored,
} from '../../../../utils/format/currency';
import TextField, { type TextFieldProps } from '../TextField/TextField';

type PresetProps = 'mask' | 'defaultValue' | 'value' | 'onChange' | 'name';

export type RhfCurrencyFieldProps<T extends FieldValues> = Omit<
  TextFieldProps,
  PresetProps | 'error' | 'helperText'
> & {
  name: Path<T>;
  helperText?: TextFieldProps['helperText'];
};

export default function RhfCurrencyField<T extends FieldValues>({
  name,
  helperText,
  onBlur: onBlurFromProps,
  ...rest
}: RhfCurrencyFieldProps<T>) {
  const { control } = useFormContext<T>();
  const { errors } = useFormState({ control, name });
  const fieldError = errors[name];
  const errorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;
  const invalid = Boolean(fieldError);

  return (
    <Controller<T>
      name={name}
      control={control}
      render={({ field }) => {
        const stored =
          field.value === undefined || field.value === null
            ? ''
            : String(field.value);

        return (
          <TextField
            {...rest}
            ref={field.ref}
            name={field.name}
            value={formatCurrencyDisplay(stored)}
            onChange={(e) => {
              field.onChange(parseCurrencyToStored(e.target.value));
            }}
            onBlur={(e) => {
              field.onBlur();
              onBlurFromProps?.(e);
            }}
            type='text'
            inputMode='decimal'
            autoComplete='off'
            error={invalid}
            helperText={invalid ? errorMessage : helperText}
          />
        );
      }}
    />
  );
}
